import {
  JSNS,
  type Message,
  type JsonObject,
  type ComposeMessage,
  type ComposeAddr,
  type AttachmentRef,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

export type { ComposeMessage, ComposeAddr, AttachmentRef } from '@zimbra-webif/types';

/** Reply context for SendMsg/SaveDraft. */
export interface ReplyContext {
  /** id of the message being replied-to / forwarded */
  origId: string;
  /** reply type: `r`=reply, `w`=forward */
  replyType: 'r' | 'w';
}

/**
 * Build a single address node `{ t, a, p? }`.
 *  t ∈ f(from) t(to) c(cc) b(bcc) r(reply-to) s(sender) n(read-receipt notify).
 * Sourced from ZmMailMsg._createMessageNode (mail.md feature 10).
 */
function addrNode(t: string, addr: ComposeAddr): JsonObject {
  const node: JsonObject = { t, a: addr.address };
  if (addr.name) node.p = addr.name;
  return node;
}

/** Map an AttachmentRef into the right shard of the `attach` node. */
interface AttachAccumulator {
  aids: string[];
  mp: JsonObject[];
  m: JsonObject[];
}

/**
 * Compose surface (urn:zimbraMail) — send / save-draft / add / bounce /
 * remove-attachments. Every method builds the `m` (or `msg`) MIME node exactly
 * as the classic DWT client does (mail.md feature 10) and emits one SOAP
 * command. Attachment *uploads* are not here — they go through the BFF upload
 * servlet (see MailApi.buildUploadUrl / docs/03-api.md); compose consumes the
 * resulting upload ids via `attachments: [{ uploadId }]`.
 */
export class ComposeApi {
  constructor(private readonly client: ZimbraClient) {}

  /**
   * SendMsgRequest — compose and send mail (new / reply / forward).
   *
   * Response: `{ m: [{ id }] }` (the sent/saved copy) when `fetchSaved` is set.
   */
  async sendMsg(
    req: ComposeMessage & {
      inReplyTo?: ReplyContext;
      identityId?: string;
      saveToSent?: boolean;
      requestReadReceipt?: boolean;
      /**
       * Scheduled send (epoch ms). ASSUMED - verify vs live capture: mail.md
       * open question 3 — deferred send may need SaveDraft+autoSendTime instead
       * of a SendMsg attr in 10.1.16. Surfaced here as `autoSendTime` on `m`.
       */
      sendAt?: number;
      fetchSaved?: boolean;
      /** sending a saved draft: Zimbra deletes that draft once the message is sent (m.did) */
      draftId?: string;
    },
  ): Promise<{ m?: Array<Pick<Message, 'id'> & Record<string, unknown>> }> {
    const m = this.buildMessageNode(req);
    if (req.draftId) m.did = req.draftId;
    if (req.inReplyTo) {
      m.origid = req.inReplyTo.origId;
      m.rt = req.inReplyTo.replyType;
    }
    if (req.identityId) m.idnt = req.identityId;
    if (req.sendAt !== undefined) m.autoSendTime = req.sendAt;

    const body: JsonObject = { m };
    if (req.saveToSent === false) body.noSave = 1;
    if (req.requestReadReceipt) {
      // read-receipt = an address node with t:"n" (mail.md feature 10).
      const e = (m.e as JsonObject[] | undefined) ?? [];
      const from = req.from?.address ?? req.replyTo?.[0]?.address;
      if (from) e.push({ t: 'n', a: from });
      m.e = e;
    }
    if (req.fetchSaved) body.fetchSavedMsg = 1;

    return this.client.call<{
      m?: Array<Pick<Message, 'id'> & Record<string, unknown>>;
    }>(JSNS.mail, 'SendMsg', body);
  }

  /**
   * SaveDraftRequest — persist an in-progress compose to Drafts (autosave +
   * explicit Save). Same `m` node as SendMsg but never delivered.
   *
   * `draftId` overwrites an existing draft; `autoSendTime` schedules a deferred
   * send (see mail.md feature 11 + open question 3).
   */
  async saveDraft(
    req: ComposeMessage & { draftId?: string; autoSendTime?: number; identityId?: string; inReplyTo?: ReplyContext },
  ): Promise<{ m?: Message[] }> {
    const m = this.buildMessageNode(req);
    if (req.draftId) m.id = req.draftId;
    if (req.inReplyTo) {
      m.origid = req.inReplyTo.origId;
      m.rt = req.inReplyTo.replyType;
    }
    if (req.autoSendTime !== undefined) m.autoSendTime = req.autoSendTime;
    if (req.identityId) m.idnt = req.identityId;
    return this.client.call<{ m?: Message[] }>(JSNS.mail, 'SaveDraft', { m });
  }

  /**
   * AddMsgRequest — insert a raw or composed message into a folder WITHOUT
   * sending (import .eml / restore). Provide either `raw` MIME or an uploaded
   * `uploadId` (.eml).
   */
  async addMsg(req: {
    folderId: string;
    raw?: string;
    uploadId?: string;
    flags?: string;
    tagNames?: string[];
    receivedDate?: number;
    filterSent?: boolean;
  }): Promise<{ m?: Message[] }> {
    const m: JsonObject = { l: req.folderId };
    if (req.flags !== undefined) m.f = req.flags;
    if (req.tagNames?.length) m.tn = req.tagNames.join(',');
    if (req.receivedDate !== undefined) m.d = req.receivedDate;
    if (req.uploadId) m.aid = req.uploadId;
    if (req.raw !== undefined) m.content = { _content: req.raw };
    const body: JsonObject = { m };
    if (req.filterSent !== undefined) body.filterSent = req.filterSent ? 1 : 0;
    return this.client.call<{ m?: Message[] }>(JSNS.mail, 'AddMsg', body);
  }

  /**
   * BounceMsgRequest — redirect an existing message to new recipients without
   * altering it (classic client "Redirect").
   */
  async bounceMsg(req: {
    id: string;
    to: ComposeAddr[];
    cc?: ComposeAddr[];
    bcc?: ComposeAddr[];
  }): Promise<JsonObject> {
    const e: JsonObject[] = [];
    req.to.forEach((a) => e.push(addrNode('t', a)));
    (req.cc ?? []).forEach((a) => e.push(addrNode('c', a)));
    (req.bcc ?? []).forEach((a) => e.push(addrNode('b', a)));
    return this.client.call(JSNS.mail, 'BounceMsg', { m: { id: req.id, e } });
  }

  /**
   * RemoveAttachmentsRequest — strip MIME parts from a message, producing a new
   * message id. Response `{ m: [{ id, ... }] }` is the rewritten message.
   */
  async removeAttachments(req: {
    id: string;
    parts: string[];
  }): Promise<{ m?: Message[] }> {
    return this.client.call<{ m?: Message[] }>(JSNS.mail, 'RemoveAttachments', {
      msg: { id: req.id, part: req.parts.join(',') },
    });
  }

  /* ============================================================== */
  /* Internal: build the shared `m` MIME node                        */
  /* ============================================================== */

  /**
   * Build the `m` node shared by SendMsg / SaveDraft.
   * Shape mirrors ZmMailMsg._createMessageNode (mail.md feature 10):
   *  - `e[]`  address nodes
   *  - `su`   subject ({_content})
   *  - `mp`   MIME tree (multipart/alternative text+html, or single part)
   *  - `attach` uploaded ids + forwarded parts/messages
   *  - `f`    flags (priority)
   */
  private buildMessageNode(req: ComposeMessage): JsonObject {
    const m: JsonObject = {};

    const e: JsonObject[] = [];
    if (req.from) e.push(addrNode('f', req.from));
    (req.to ?? []).forEach((a) => e.push(addrNode('t', a)));
    (req.cc ?? []).forEach((a) => e.push(addrNode('c', a)));
    (req.bcc ?? []).forEach((a) => e.push(addrNode('b', a)));
    (req.replyTo ?? []).forEach((a) => e.push(addrNode('r', a)));
    if (e.length) m.e = e;

    if (req.subject !== undefined) m.su = { _content: req.subject };

    const mp = this.buildMimeNode(req.text, req.html);
    if (mp) m.mp = [mp];

    const attach = this.buildAttachNode(req.attachments);
    if (attach) m.attach = attach;

    if (req.priority) m.f = req.priority;

    return m;
  }

  /** Build a text/html MIME node (multipart/alternative when both present). */
  private buildMimeNode(text?: string, html?: string): JsonObject | undefined {
    const parts: JsonObject[] = [];
    if (text !== undefined) parts.push({ ct: 'text/plain', content: { _content: text } });
    if (html !== undefined) parts.push({ ct: 'text/html', content: { _content: html } });
    if (parts.length === 0) return undefined;
    if (parts.length === 1) return parts[0];
    return { ct: 'multipart/alternative', mp: parts };
  }

  /** Build the `attach` node from AttachmentRefs (uploads + forwarded parts/msgs). */
  private buildAttachNode(refs?: AttachmentRef[]): JsonObject | undefined {
    if (!refs || refs.length === 0) return undefined;
    const acc: AttachAccumulator = { aids: [], mp: [], m: [] };
    for (const ref of refs) {
      if ('uploadId' in ref) {
        acc.aids.push(ref.uploadId);
      } else if ('part' in ref) {
        acc.mp.push({ mid: ref.messageId, part: ref.part });
      } else {
        acc.m.push({ id: ref.messageId });
      }
    }
    const attach: JsonObject = {};
    if (acc.aids.length) attach.aid = acc.aids.join(',');
    if (acc.mp.length) attach.mp = acc.mp;
    if (acc.m.length) attach.m = acc.m;
    return attach;
  }
}
